/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import moment from 'moment';
import { fillNC4Form } from '../../../../services/NC4PDFService';
import { NC4FormData } from '../../NewHire/FormNC4Modal';

interface NC4PreviewModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  uploadedPdf?: string;
  formData: NC4FormData | null;
}

const NC4PreviewModal: React.FC<NC4PreviewModalProps> = ({ isOpen, toggleModal, uploadedPdf, formData }) => {
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const staticNc4PdfFileUrl =
    uploadedPdf || 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/nc-state-w4.pdf';

  const generatePdf = useCallback(async () => {
    const dataToFill: any = formData || {};

    if (!dataToFill) {
      setError('Form data is not available to generate the PDF.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }

    try {
      const filledPdfBytes = await fillNC4Form(staticNc4PdfFileUrl, dataToFill);
      if (filledPdfBytes) {
        const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
        const docUrl = URL.createObjectURL(blob);
        setPdfUrl(docUrl);
      } else {
        setError('Failed to generate the filled PDF bytes for NC-4.');
      }
    } catch (e) {
      console.error('Error generating NC-4 PDF preview:', e);
      setError(e instanceof Error ? e.message : 'An unknown error occurred while generating NC-4 PDF.');
    } finally {
      setIsLoading(false);
    }
  }, [pdfUrl, formData]);

  useEffect(() => {
    if (isOpen) {
      generatePdf();
    } else {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl('');
      }
      setError(null);
      setIsLoading(false);
    }
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen]);

  const handleDownload = () => {
    if (!pdfUrl) {
      console.error('NC-4 PDF URL or Form Data not available for download.');
      return;
    }
    const link = document.createElement('a');
    link.href = pdfUrl;
    const lastNameForFile = formData?.lastName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'employee';
    const yearForFile = moment(formData?.signatureDate, 'MM/DD/YYYY').isValid()
      ? moment(formData?.signatureDate, 'MM/DD/YYYY').format('YYYY')
      : moment().format('YYYY');
    link.download = `NC-4_${lastNameForFile}_${yearForFile}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={toggleModal} backdrop="static">
      <ModalHeader className="pb-1" toggle={toggleModal}>
        NC Withholding Allowance Preview
      </ModalHeader>
      <ModalBody className="pt-0">
        <Row className="mt-2">
          <Col md="12">
            <div className="d-flex justify-content-end mb-2">
              <Button color="primary" onClick={handleDownload} size="sm" disabled={!pdfUrl || isLoading || !!error}>
                Download PDF
              </Button>
            </div>
          </Col>
          <Col
            md="12"
            style={{
              minHeight: '500px',
              maxHeight: '70vh',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              position: 'relative',
            }}
          >
            {isLoading && (
              <div
                className="text-center p-5"
                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              >
                <Spinner style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-2">Generating PDF...</p>
              </div>
            )}
            {error && !isLoading && <div className="text-center text-danger p-3">Error generating PDF: {error}</div>}
            {!isLoading && !error && pdfUrl && (
              <iframe
                src={pdfUrl}
                style={{ width: '100%', height: 'calc(70vh - 20px)', border: 'none' }}
                title="NC Withholding Allowance Preview"
              />
            )}
            {!isLoading && !error && !pdfUrl && isOpen && (
              <div className="text-center p-3">Preparing PDF preview...</div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NC4PreviewModal;
